import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { IoMdArrowBack } from "react-icons/io";
import { FiRefreshCw, FiSearch } from "react-icons/fi";
import DataTable from "react-data-table-component";
import Loader from "../../components/loader/loader";
import { getAdminBillingVendors } from "../../api/context/billing";

const vendorsDatatableCustomStyles = {
    header: {
        style: {
            fontWeight: "600",
            fontSize: "22px",
            color: "#000",
        },
    },
    headCells: {
        style: {
            display: "flex",
            justifyContent: "center",
            fontWeight: "600",
            fontSize: "13px",
            padding: "16px 8px",
            backgroundColor: "#f9f9f9",
        },
    },
    cells: {
        style: {
            display: "flex",
            fontSize: "12px",
            fontWeight: "500",
            padding: "12px",
            justifyContent: "center",
            textAlign: "center",
        },
    },
};

const PLAN_OPTIONS = [
    { value: "boutique", label: "Boutique" },
    { value: "label", label: "Label" },
    { value: "brand", label: "Brand" },
];

const STATUS_OPTIONS = [
    { value: "active", label: "Active" },
    { value: "pending", label: "Pending" },
    { value: "past_due", label: "Past Due" },
    { value: "cancelled", label: "Cancelled" },
    { value: "expired", label: "Expired" },
    { value: "none", label: "No Subscription" },
];

const TRIAL_OPTIONS = [
    { value: "true", label: "Trial Claimed" },
    { value: "false", label: "Trial Not Claimed" },
];

const STATUS_BADGE_STYLES = {
    active: { bg: "#dcfce7", color: "#15803d" },
    pending: { bg: "#fef3c7", color: "#b45309" },
    past_due: { bg: "#fee2e2", color: "#b91c1c" },
    cancelled: { bg: "#f1f5f9", color: "#475569" },
    expired: { bg: "#e5e7eb", color: "#374151" },
    default: { bg: "#f1f5f9", color: "#475569" },
};

const PLAN_BADGE_STYLES = {
    boutique: { bg: "#ede9fe", color: "#6d28d9" },
    label: { bg: "#dbeafe", color: "#1d4ed8" },
    brand: { bg: "#fce7f3", color: "#be185d" },
    default: { bg: "#f1f5f9", color: "#475569" },
};

const formatCurrency = (value) => {
    const number = Number(value || 0);
    return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
    }).format(number);
};

const formatNumber = (value) => new Intl.NumberFormat("en-IN").format(Number(value || 0));

const humanize = (value) => {
    if (!value) return "—";
    return value
        .split("_")
        .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
        .join(" ");
};

const AiListingVendors = () => {
    const navigate = useNavigate();

    const [filters, setFilters] = useState({
        planCode: "",
        subscriptionStatus: "",
        hasClaimedTrial: "",
        search: "",
    });
    const [searchInput, setSearchInput] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const fetchVendors = useCallback(
        async (isRefresh = false, overrides = {}) => {
            if (isRefresh) setRefreshing(true);
            else setLoading(true);

            const effective = { ...filters, ...overrides };
            const requestPage = overrides.page ?? page;
            const requestPageSize = overrides.pageSize ?? pageSize;

            const params = {
                page: requestPage,
                pageSize: requestPageSize,
                sortBy: "vendorId",
                sortOrder: "desc",
            };
            if (effective.planCode) params.planCode = effective.planCode;
            if (effective.subscriptionStatus) params.subscriptionStatus = effective.subscriptionStatus;
            if (effective.hasClaimedTrial) params.hasClaimedTrial = effective.hasClaimedTrial;
            if (effective.search) params.search = effective.search;

            try {
                const response = await getAdminBillingVendors(params);
                if (response?.data?.statusCode === 200 || response?.status === 200) {
                    const payload = response?.data?.data || response?.data;
                    setData(payload);
                } else {
                    setData(null);
                    toast.error("Failed to fetch vendors");
                }
            } catch (error) {
                console.error("Error fetching admin vendors:", error);
                if (error?.response?.status === 403) {
                    toast.error("You don't have permission to view vendors");
                } else {
                    toast.error("Failed to fetch vendors");
                }
                setData(null);
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [filters, page, pageSize]
    );

    useEffect(() => {
        fetchVendors();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize]);

    useEffect(() => {
        setPage(1);
        const timer = setTimeout(() => fetchVendors(true, { page: 1 }), 300);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.planCode, filters.subscriptionStatus, filters.hasClaimedTrial, filters.search]);

    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const handleSearchChange = (value) => {
        setSearchInput(value);
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setFilters((prev) => (prev.search === searchInput ? prev : { ...prev, search: searchInput }));
        }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const handleClearFilters = () => {
        setFilters({ planCode: "", subscriptionStatus: "", hasClaimedTrial: "", search: "" });
        setSearchInput("");
        setPage(1);
    };

    const isFilterActive = useMemo(
        () =>
            filters.planCode !== "" ||
            filters.subscriptionStatus !== "" ||
            filters.hasClaimedTrial !== "" ||
            filters.search !== "",
        [filters]
    );

    const vendors = Array.isArray(data?.vendors) ? data.vendors : [];
    const totalRows = data?.pagination?.total || 0;

    const columns = useMemo(
        () => [
            {
                name: "Vendor",
                center: true,
                minWidth: "220px",
                grow: 2,
                cell: (row) => (
                    <div className="d-flex align-items-center justify-content-center gap-2" style={{ width: "100%" }}>
                        {row.image ? (
                            <img
                                src={row.image}
                                alt={row.storeName}
                                className="rounded-circle flex-shrink-0"
                                style={{ width: 32, height: 32, objectFit: "cover", border: "1px solid #eef2f7" }}
                            />
                        ) : (
                            <div
                                className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 fw-bold text-muted"
                                style={{ width: 32, height: 32, background: "#f1f5f9", fontSize: 12 }}
                            >
                                {(row.storeName || "?").charAt(0).toUpperCase()}
                            </div>
                        )}
                        <span
                            className="fw-semibold text-truncate"
                            style={{ maxWidth: 160 }}
                            title={row.storeName}
                        >
                            {row.storeName || "—"}
                        </span>
                    </div>
                ),
            },
            {
                name: "Plan",
                center: true,
                minWidth: "220px",
                cell: (row) => {
                    const sub = row.currentSubscription;
                    const next = row.scheduledNextPlan;

                    if (!sub && !next) return <span className="text-muted">No plan</span>;

                    const PILL_WIDTH = 200;

                    if (sub && next) {
                        const cur = PLAN_BADGE_STYLES[sub.planCode] || PLAN_BADGE_STYLES.default;
                        const nxt = PLAN_BADGE_STYLES[next.planCode] || PLAN_BADGE_STYLES.default;
                        return (
                            <div
                                className="d-flex align-items-stretch"
                                style={{
                                    width: PILL_WIDTH,
                                    borderRadius: 6,
                                    overflow: "hidden",
                                    border: "1px solid #e5e7eb",
                                    fontSize: 11,
                                    fontWeight: 600,
                                }}
                            >
                                <div
                                    style={{
                                        flex: 1,
                                        background: cur.bg,
                                        color: cur.color,
                                        padding: "6px 8px",
                                        textAlign: "center",
                                    }}
                                    title={`Current: ${sub.planName || humanize(sub.planCode)}`}
                                >
                                    {sub.planName || humanize(sub.planCode)}
                                </div>
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{
                                        background: "#fff",
                                        color: "#94a3b8",
                                        padding: "0 6px",
                                        fontSize: 13,
                                    }}
                                >
                                    ⇄
                                </div>
                                <div
                                    style={{
                                        flex: 1,
                                        background: nxt.bg,
                                        color: nxt.color,
                                        padding: "6px 8px",
                                        textAlign: "center",
                                    }}
                                    title={`Next: ${next.planName || humanize(next.planCode)}`}
                                >
                                    {next.planName || humanize(next.planCode)}
                                </div>
                            </div>
                        );
                    }

                    const single = sub || next;
                    const style = PLAN_BADGE_STYLES[single.planCode] || PLAN_BADGE_STYLES.default;
                    return (
                        <div
                            style={{
                                width: PILL_WIDTH,
                                borderRadius: 6,
                                background: style.bg,
                                color: style.color,
                                padding: "6px 8px",
                                textAlign: "center",
                                fontSize: 11,
                                fontWeight: 600,
                            }}
                        >
                            {single.planName || humanize(single.planCode)}
                        </div>
                    );
                },
            },
            {
                name: "Sub Status",
                center: true,
                minWidth: "130px",
                cell: (row) => {
                    const sub = row.currentSubscription;
                    if (!sub) return <span className="text-muted">—</span>;
                    const style = STATUS_BADGE_STYLES[sub.status] || STATUS_BADGE_STYLES.default;
                    return (
                        <div className="d-flex flex-column align-items-center gap-1">
                            <span
                                className="badge rounded-pill"
                                style={{
                                    background: style.bg,
                                    color: style.color,
                                    fontWeight: 600,
                                    fontSize: 11,
                                    padding: "6px 10px",
                                }}
                            >
                                {humanize(sub.status)}
                            </span>
                            {sub.cancelAtPeriodEnd && (
                                <small className="text-danger" style={{ fontSize: 10 }}>Cancels at period end</small>
                            )}
                        </div>
                    );
                },
            },
            {
                name: "Listings (This Period)",
                center: true,
                minWidth: "150px",
                cell: (row) => {
                    const sub = row.currentSubscription;
                    if (sub) {
                        const used = Number(sub.listingsUsedThisPeriod || 0);
                        const quota = Number(sub.monthlyQuota || 0);
                        return (
                            <div className="d-flex flex-column align-items-center">
                                <span className="fw-bold">
                                    {formatNumber(used)} / {formatNumber(quota)}
                                </span>
                                <small className="text-muted">
                                    {formatNumber(sub.listingsRemaining)} remaining
                                </small>
                            </div>
                        );
                    }
                    return (
                        <div className="d-flex flex-column align-items-center">
                            <span className="fw-bold">{formatNumber(row?.usage?.listingsThisMonth)}</span>
                            <small className="text-muted">this month</small>
                        </div>
                    );
                },
            },
            {
                name: "Wallet",
                center: true,
                minWidth: "110px",
                cell: (row) => {
                    const wallet = row.wallet;
                    if (!wallet) return <span className="text-muted">—</span>;
                    return (
                        <div className="d-flex flex-column align-items-center">
                            <span className="fw-bold">{formatNumber(wallet.balance)}</span>
                            <small className="text-muted">balance</small>
                        </div>
                    );
                },
            },
            {
                name: "Total Listings",
                center: true,
                minWidth: "110px",
                cell: (row) => (
                    <span className="fw-semibold">{formatNumber(row?.usage?.listingsAllTime)}</span>
                ),
            },
            {
                name: "Total Spend",
                center: true,
                minWidth: "130px",
                cell: (row) => {
                    const spend = Number(row?.financials?.totalSpendInr || 0);
                    return spend > 0 ? (
                        <span className="fw-bold">{formatCurrency(spend)}</span>
                    ) : (
                        <span className="text-muted">—</span>
                    );
                },
            },
            {
                name: "Trial",
                center: true,
                minWidth: "110px",
                cell: (row) => {
                    if (!row?.trial?.claimed) return <span className="text-muted">—</span>;
                    return (
                        <div className="d-flex flex-column align-items-center gap-1">
                            <span
                                className="badge rounded-pill"
                                style={{
                                    background: "#dcfce7",
                                    color: "#15803d",
                                    fontWeight: 600,
                                    fontSize: 11,
                                    padding: "6px 10px",
                                }}
                            >
                                Claimed
                            </span>
                            <small className="text-muted">
                                {formatNumber(row.trial.creditsGranted)} credits
                            </small>
                        </div>
                    );
                },
            },
        ],
        []
    );

    const controlHeight = 40;

    return (
        <div className="container-fluid p-4 min-vh-100">
            {/* Header (two rows) */}
            <div
                style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1000,
                    background: "#f9fafb",
                    margin: "-44px -48px 20px",
                    padding: "40px 48px 24px",
                }}
            >
                {/* Row 1 — title + refresh */}
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                    <div className="d-flex align-items-center mb-0">
                        <div
                            onClick={() => navigate(-1)}
                            style={{
                                cursor: "pointer",
                                fontSize: "24px",
                                fontWeight: "800",
                                display: "flex",
                                alignItems: "center",
                                marginRight: "15px",
                            }}
                        >
                            <IoMdArrowBack className="icon" />
                        </div>
                        <h4 className="mb-0 fw-bold">AI Listing Vendors</h4>
                    </div>

                    <button
                        onClick={() => fetchVendors(true)}
                        className="btn border border-secondary bg-transparent rounded-3 d-flex align-items-center gap-2 px-3"
                        style={{ height: controlHeight }}
                        disabled={refreshing}
                    >
                        <FiRefreshCw
                            className="text-secondary"
                            size={18}
                            style={{ animation: refreshing ? "spin 1s linear infinite" : "none" }}
                        />
                        <span className="text-secondary">{refreshing ? "Refreshing..." : "Refresh"}</span>
                    </button>
                </div>

                {/* Row 2 — search + plan + status + trial + clear */}
                <div className="d-flex align-items-center gap-2 flex-wrap mt-3">
                    <div className="position-relative" style={{ flex: "1 1 320px", minWidth: 260 }}>
                        <FiSearch
                            className="position-absolute text-muted"
                            style={{ top: "50%", left: 12, transform: "translateY(-50%)", fontSize: 15 }}
                        />
                        <input
                            type="text"
                            className="form-control"
                            style={{ paddingLeft: 34, height: controlHeight, fontSize: 13, borderRadius: 8, width: "100%" }}
                            placeholder="Search vendor by store name"
                            value={searchInput}
                            onChange={(e) => handleSearchChange(e.target.value)}
                        />
                    </div>

                    <select
                        className="form-select flex-shrink-0"
                        style={{ width: 160, height: controlHeight, fontSize: 13 }}
                        value={filters.planCode}
                        onChange={(e) => handleFilterChange("planCode", e.target.value)}
                    >
                        <option value="">All plans</option>
                        {PLAN_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>

                    <select
                        className="form-select flex-shrink-0"
                        style={{ width: 180, height: controlHeight, fontSize: 13 }}
                        value={filters.subscriptionStatus}
                        onChange={(e) => handleFilterChange("subscriptionStatus", e.target.value)}
                    >
                        <option value="">All statuses</option>
                        {STATUS_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>

                    <select
                        className="form-select flex-shrink-0"
                        style={{ width: 180, height: controlHeight, fontSize: 13 }}
                        value={filters.hasClaimedTrial}
                        onChange={(e) => handleFilterChange("hasClaimedTrial", e.target.value)}
                    >
                        <option value="">All trials</option>
                        {TRIAL_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>

                    {isFilterActive && (
                        <button
                            onClick={handleClearFilters}
                            className="btn btn-outline-secondary flex-shrink-0"
                            style={{ height: controlHeight, fontSize: 13 }}
                            disabled={refreshing}
                        >
                            Clear
                        </button>
                    )}
                </div>
            </div>

            {/* Vendors table */}
            <div className="shadow-none border border-blue-grey-200 rounded-3" style={{ overflowX: "auto" }}>
                <DataTable
                    columns={columns}
                    data={vendors}
                    progressPending={loading || refreshing}
                    progressComponent={
                        <div style={{ height: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Loader />
                        </div>
                    }
                    noDataComponent={
                        <div className="d-flex flex-column align-items-center justify-content-center text-center text-muted p-5">
                            <h6 className="fw-bold mb-2">No vendors found</h6>
                            <small className="mb-0" style={{ maxWidth: 420 }}>
                                Try adjusting the filters or clearing the search.
                            </small>
                        </div>
                    }
                    highlightOnHover
                    pointerOnHover
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={pageSize}
                    paginationRowsPerPageOptions={[10, 20, 50, 100]}
                    paginationDefaultPage={page}
                    onChangeRowsPerPage={(newPerPage, newPage) => {
                        setPageSize(newPerPage);
                        setPage(newPage);
                    }}
                    onChangePage={(newPage) => setPage(newPage)}
                    customStyles={vendorsDatatableCustomStyles}
                />
            </div>

            <style>
                {`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                `}
            </style>
        </div>
    );
};

export default AiListingVendors;
